<script setup>
import { computed } from 'vue';
import {
  FileText,
  FileSpreadsheet,
  FileCode,
  FileJson,
  FileType,
  FileImage,
  File,
  FileArchive,
} from 'lucide-vue-next';
import Tooltip2 from '@/components/Tooltip2.vue';
import ImageLazy from '@/components/ImageLazy.vue';

const props = defineProps({
  asset: Object,
});

const emit = defineEmits(['click']);

function onClick(payload) {
  emit('click', payload);
}

const extension = computed(() => {
  const name = props.asset.name || props.asset.meta?.filename || '';
  const parts = name.split('.');
  return parts.length > 1 ? parts.pop().toUpperCase() : '';
});

// Configure style and icon based on file format
const documentStyle = computed(() => {
  const format = props.asset?.meta?.format?.toLowerCase() || extension.value.toLowerCase();

  switch (format) {
    case 'pdf':
      return {
        icon: FileText,
        color: 'text-rose-500',
        bg: 'bg-rose-50',
        label: 'PDF',
      };
    case 'doc':
    case 'docx':
      return {
        icon: FileText,
        color: 'text-blue-500',
        bg: 'bg-blue-50',
        label: 'DOC',
      };
    case 'xls':
    case 'xlsx':
    case 'csv':
      return {
        icon: FileSpreadsheet,
        color: 'text-emerald-500',
        bg: 'bg-emerald-50',
        label: 'XLS',
      };
    case 'ppt':
    case 'pptx':
      return {
        icon: FileType,
        color: 'text-orange-500',
        bg: 'bg-orange-50',
        label: 'PPT',
      };
    case 'txt':
    case 'md':
      return {
        icon: FileText,
        color: 'text-slate-500',
        bg: 'bg-slate-50',
        label: format.toUpperCase() || 'TXT',
      };
    case 'json':
      return {
        icon: FileJson,
        color: 'text-amber-500',
        bg: 'bg-amber-50',
        label: 'JSON',
      };
    case 'js':
    case 'ts':
    case 'vue':
    case 'html':
    case 'css':
      return {
        icon: FileCode,
        color: 'text-cyan-500',
        bg: 'bg-cyan-50',
        label: 'CODE',
      };
    case 'zip':
    case 'rar':
    case '7z':
      return {
        icon: FileArchive,
        color: 'text-violet-500',
        bg: 'bg-violet-50',
        label: 'ZIP',
      };
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'webp':
    case 'svg':
      return {
        icon: FileImage,
        color: 'text-indigo-500',
        bg: 'bg-indigo-50',
        label: 'IMG',
      };
    default:
      return {
        icon: File,
        color: 'text-black/40',
        bg: 'bg-black/5',
        label: extension.value || 'FILE',
      };
  }
});

const isDocument = computed(() => props.asset.source === 'document');
</script>

<template>
  <Tooltip2 :text="asset.name || 'Untitled Asset'">
    <div
      class="group relative aspect-square rounded-xl overflow-hidden border transition-all duration-300 cursor-pointer bg-white select-none"
      :class="[
        isDocument
          ? `border-black/5 hover:border-black/10 hover:shadow-lg hover:-translate-y-1`
          : 'border-black/5 hover:border-blue-400 hover:shadow-md',
      ]"
      @click="onClick(asset)"
    >
      <!-- Document Layout -->
      <div v-if="isDocument" class="w-full h-full flex flex-col">
        <!-- Icon Area -->
        <div
          class="flex-1 flex items-center justify-center relative overflow-hidden"
          :class="[documentStyle.bg, documentStyle.color]"
        >
          <!-- Decorative pattern/circle using currentColor -->
          <div
            class="absolute inset-0 opacity-20"
            style="
              background-image: radial-gradient(currentColor 1px, transparent 1px);
              background-size: 12px 12px;
            "
          ></div>

          <!-- Main Icon -->
          <component
            :is="documentStyle.icon"
            class="w-10 h-10 relative z-10 transform group-hover:scale-110 transition-transform duration-300"
            stroke-width="1.5"
          />

          <!-- Type Badge (Pill) -->
          <div
            class="absolute top-2 right-2 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide opacity-0 group-hover:opacity-100 transition-opacity duration-200 shadow-sm bg-white/80 backdrop-blur-sm"
          >
            {{ documentStyle.label }}
          </div>
        </div>

        <!-- Info Area -->
        <div class="h-10 px-3 flex items-center bg-white border-t border-black/5/50 relative">
          <div class="w-full">
            <p class="text-xs text-black/70 font-medium truncate leading-tight">
              {{ asset.name || 'Untitled' }}
            </p>
          </div>
        </div>
      </div>

      <!-- Image Layout -->
      <template v-else>
        <ImageLazy
          :src="asset.url"
          :alt="asset.name || 'Asset'"
          class="w-full h-full"
          image-class="object-cover bg-black/5 transition-transform duration-500 group-hover:scale-110"
        />
        <!-- Gradient Overlay -->
        <div
          class="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        >
          <div class="absolute bottom-0 left-0 w-full p-2">
            <p class="text-white text-[10px] truncate font-medium drop-shadow-md">
              {{ asset.name }}
            </p>
          </div>
        </div>

        <!-- Selection/Hover ring effect -->
        <div
          class="absolute inset-0 border-2 border-blue-500 opacity-0 group-hover:opacity-100 rounded-xl transition-opacity duration-200 pointer-events-none"
        ></div>
      </template>
    </div>
  </Tooltip2>
</template>
